import {
  PropsWithChildren, RefObject, useCallback, useEffect, useState,
} from 'react';
import {createPortal} from 'react-dom';

export type PortalProps = {
  id?: string;
  className?: string;
  style?: Partial<CSSStyleDeclaration>;
  outsideClickOmitElements?: Array<RefObject<unknown>>;
  onOutsideClick?: (evt?: MouseEvent) => void;
};

export function Portal(props: PropsWithChildren<PortalProps>): React.ReactPortal {
  const [container] = useState(() => document.createElement('div'));
  const {className, id, onOutsideClick, style, outsideClickOmitElements = []} = props;

  const outsideClick = useCallback((evt: MouseEvent) => {
    const elementsToCheck = [container]
      .concat(outsideClickOmitElements
        .map(({current}) => current as HTMLDivElement)
        .filter(x => x !== null));
    if (onOutsideClick && !elementsToCheck.some(el => el.contains(evt?.target as any))) {
      onOutsideClick(evt);
    }
  }, [container, outsideClickOmitElements, onOutsideClick]);

  useEffect(() => {
    if (style == null) {
      container.setAttribute('style', '');
    } else {
      container.setAttribute('style', '');
      for (const cssProperty in style) {
        if (Object.hasOwn(style, cssProperty)) {
          container.style[cssProperty] = style[cssProperty]!;
        }
      }
    }
  }, [container, style]);

  useEffect(() => {
    if (id) {
      container.setAttribute('id', id);
    } else {
      container.removeAttribute('id');
    }
  }, [container, id]);

  useEffect(() => {
    if (className) {
      container.setAttribute('class', className);
    } else {
      container.removeAttribute('class');
    }
  }, [container, className]);

  useEffect(() => {
    document.body.addEventListener('click', outsideClick);
    return () => {
      document.body.removeEventListener('click', outsideClick);
    };
  }, [outsideClick]);

  useEffect(() => {
    document.body.append(container);
    return () => {
      container.remove();
      document.body.removeEventListener('click', outsideClick);
    };
  }, []);

  return createPortal(props.children, container);
}
